import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, MapPin, UserCircle2, BadgeCheck } from "lucide-react";
import { TIER_THEMES } from "@/lib/themes";

export default function Discover() {
  const [industries, setIndustries] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filters, setFilters] = useState({ industry: "any", role: "any", q: "", phone: "", members_only: false });
  const [results, setResults] = useState([]);
  const [discoveryMode, setDiscoveryMode] = useState(null);
  const [lockReason, setLockReason] = useState(null);
  const [busy, setBusy] = useState(false);
  const [geoHint, setGeoHint] = useState(null);

  // State-code → suggested industry filter
  const STATE_TO_INDUSTRY = {
    TN: "kollywood", KA: "sandalwood", TS: "tollywood", AP: "tollywood",
    KL: "mollywood", WB: "bengali",
    MH: "bollywood", DL: "bollywood", GJ: "bollywood",
  };

  useEffect(() => {
    api.get("/meta/industries").then(({ data }) => setIndustries(data));
    api.get("/meta/roles").then(({ data }) => setRoles(data));
    runSearch({ industry: "any", role: "any", q: "" });

    // Try browser geolocation → reverse-infer industry filter suggestion
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=5`);
          const j = await r.json();
          const stateName = j?.address?.state || "";
          const stateMap = {
            "Maharashtra":"MH","Tamil Nadu":"TN","Karnataka":"KA","Telangana":"TS",
            "Andhra Pradesh":"AP","West Bengal":"WB","Kerala":"KL","Delhi":"DL",
            "Gujarat":"GJ","Rajasthan":"RJ",
          };
          const sc = stateMap[stateName];
          const ind = STATE_TO_INDUSTRY[sc];
          if (ind && ind !== "bollywood") {
            setGeoHint({ state_name: stateName, industry: ind });
          }
        } catch (geoErr) {
          console.warn("[Discover] reverse-geocode failed:", geoErr);
        }
      }, (permErr) => { console.warn("[Discover] geolocation denied:", permErr?.message); }, { timeout: 4000 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async (f = filters) => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (f.industry && f.industry !== "any") params.set("industry", f.industry);
      if (f.role && f.role !== "any") params.set("role", f.role);
      if (f.q) params.set("q", f.q);
      if (f.phone) params.set("phone", f.phone);
      if (f.members_only) params.set("members_only", "true");
      const { data } = await api.get(`/discovery/search?${params.toString()}`);
      setResults(data.items || []);
      setDiscoveryMode(data.discovery_mode || null);
      setLockReason(data.lock_reason || null);
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="discover-page">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Talent Network</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">Discover the Industry</h1>
      <p className="mt-3 text-sm text-white/55 max-w-2xl">
        Verified crew across Bollywood, Kollywood, Tollywood, Sandalwood, Mollywood, Bengali cinema.
        Filter by industry + role, then connect.
      </p>

      {/* Geo hint banner */}
      {geoHint && (
        <div className="mt-6 cs-card rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3"
             data-testid="geo-hint">
          <div className="text-sm text-white/75">
            Detected you in <span className="font-semibold">{geoHint.state_name}</span> — show{" "}
            <span className="uppercase tracking-[0.2em]" style={{ color: "var(--association-theme)" }}>
              {industries.find(i => i.key === geoHint.industry)?.label}
            </span> first?
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { const f = { ...filters, industry: geoHint.industry }; setFilters(f); runSearch(f); setGeoHint(null); }}
              className="btn-themed h-9 px-4 rounded-full uppercase tracking-[0.22em] text-[10px]"
              data-testid="geo-yes">
              Yes, filter
            </Button>
            <Button onClick={() => setGeoHint(null)} variant="outline"
              className="h-9 px-4 rounded-full uppercase tracking-[0.22em] text-[10px] bg-transparent border-white/20 hover:bg-white hover:text-black"
              data-testid="geo-no">
              Skip
            </Button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="mt-8 cs-card rounded-2xl p-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">Industry</Label>
            <Select value={filters.industry} onValueChange={(v) => setFilters({ ...filters, industry: v })}>
              <SelectTrigger className="bg-black/40 border-white/15 mt-1.5" data-testid="f-industry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any industry</SelectItem>
                {industries.map((i) => <SelectItem key={i.key} value={i.key}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">Role</Label>
            <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
              <SelectTrigger className="bg-black/40 border-white/15 mt-1.5" data-testid="f-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="any">Any role</SelectItem>
                {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2 flex gap-2">
            <div className="flex-1">
              <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">Name or Phone</Label>
              <Input value={filters.q || filters.phone} placeholder="Type a name or phone (10 digits)…"
                onChange={(e) => {
                  const v = e.target.value;
                  const isPhone = /^[+\d\s\-()]{6,}$/.test(v) && /\d{6,}/.test(v);
                  setFilters(isPhone
                    ? { ...filters, q: "", phone: v }
                    : { ...filters, q: v, phone: "" });
                }}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                className="bg-black/40 border-white/15 mt-1.5" data-testid="f-q" />
            </div>
            <Button onClick={() => runSearch()} disabled={busy}
              className="btn-themed h-10 self-end px-5 rounded-full uppercase tracking-[0.25em] text-xs"
              data-testid="f-search">
              <Search size={14} className="mr-2" /> Search
            </Button>
          </div>
        </div>
        {/* Members-only toggle */}
        <label className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/65 cursor-pointer select-none"
               data-testid="f-members-toggle">
          <input type="checkbox" checked={filters.members_only}
                 onChange={(e) => { const f = { ...filters, members_only: e.target.checked }; setFilters(f); runSearch(f); }}
                 className="accent-[color:var(--association-theme)] w-4 h-4" />
          Verified association members only
        </label>
      </div>

      {/* Results */}
      <div className="mt-6">
        {discoveryMode === "locked" && (
          <div className="cs-card rounded-2xl p-8 text-center border border-yellow-500/30 bg-yellow-500/5"
               data-testid="discovery-locked">
            <div className="text-[10px] tracking-[0.3em] uppercase text-yellow-400 mb-2">Discovery Locked</div>
            <div className="text-sm text-white/75 max-w-xl mx-auto">{lockReason}</div>
          </div>
        )}
        {discoveryMode === "scoped_to_tier" && (
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-3" data-testid="discovery-scoped">
            Scoped discovery · You can only see your own department tier
          </div>
        )}
        {discoveryMode !== "locked" && (
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-3">
            {results.length} verified profile{results.length === 1 ? "" : "s"}
          </div>
        )}
        {results.length === 0 ? (
          <div className="cs-card rounded-2xl p-10 text-center text-white/50 text-sm">
            No profiles match those filters yet. As more crew sign up across industries, this view will fill in.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="result-grid">
            {results.map((p) => <ProfileCard key={p.user_id} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileCard({ p }) {
  const tier = TIER_THEMES[p.tier];
  return (
    <Link to={`/profile/${p.user_id}`} className="cs-card rounded-2xl p-5 hover:translate-y-[-2px] transition group"
          data-testid={`profile-card-${p.user_id}`}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-white/5 border border-white/15 flex items-center justify-center overflow-hidden">
          {p.photo_url
            ? <img src={p.photo_url} alt={p.full_name} className="w-full h-full object-cover" />
            : <UserCircle2 size={28} className="text-white/40" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base uppercase tracking-tight truncate flex items-center gap-1.5">
            {p.full_name}
            {p.verified && <BadgeCheck size={14} style={{ color: "var(--association-theme)" }} />}
          </div>
          <div className="text-xs text-white/55 truncate">{p.role_label}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/55">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: tier?.color }} />
        <span>{p.association_name || "—"}</span>
        {p.is_member ? (
          <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 tracking-[0.18em]">MEMBER</span>
        ) : (
          <span className="px-1.5 py-0.5 rounded bg-white/5 text-white/45 tracking-[0.18em] border border-white/10">NON-MEMBER</span>
        )}
        {p.home_city && <><span className="text-white/30">·</span><MapPin size={11} /><span>{p.home_city}</span></>}
      </div>
      {p.bio && <div className="mt-3 text-xs text-white/55 line-clamp-2">{p.bio}</div>}
      <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
        <div className="text-[10px] tracking-[0.25em] uppercase text-white/45">
          {(p.past_credits || []).length + (p.auto_credits || []).length} credits
        </div>
        <span className="text-[10px] tracking-[0.25em] uppercase font-medium" style={{ color: "var(--association-theme)" }}>
          View profile →
        </span>
      </div>
    </Link>
  );
}
